import { Autocomplete, Box, Button, CircularProgress, Divider, InputAdornment, Paper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { BeaconEditorModes } from "../experience/ExperienceEditor";
import { useAppDispatch } from "../../../../utils/hooks";
import { createRealEstateListing, fetchError, getMyBusinessGroups, setCurrentBusinessGroup, showMessage } from "../../../../redux/actions";
import ReactCountryFlag from "react-country-flag";
import SearchIcon from '@mui/icons-material/Search';
import LanguageIcon from '@mui/icons-material/Language';
import { Coordinate, GeocoderResponse, Place } from "../../../../types/models/Mapkit";
import { AppColor, Fonts } from "../../../../types/Constants";
import { useMap } from 'react-mapkit';
import jwtAxios from "../../../../utils/jwtAxios";

type RealEstateListingProps = {
    mode?: typeof BeaconEditorModes[number];
    cancelSettingsForm: () => void;
    onComplete: (newGroupId: string) => void;
};
    
const RealEstateListing: React.FC<RealEstateListingProps> = (prop) => {

    const dispatch = useAppDispatch();
    const themeApp = useTheme();
    const fullscreen = useMediaQuery(themeApp.breakpoints.down('md'));
    const { mapkit } = useMap();


    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [requestLoading, setRequestLoading] = useState<boolean>(false);
    const [lookupAddress, setLookupAddress] = useState<string>();
    const [selectedLookupAddressOption, setSelectedLookupAddressOption] = useState<Place | null>(null);
    const [lookupAddressOptions, setLookupAddressOptions] = useState<Place[]>([]);
    const [userLocation, setUserLocation] = useState<Coordinate>();
    const [url, setUrl] = useState<string>("");

    const [jobMessage, setJobMessage] = useState<string>("");

    useEffect(() => {
        if (!lookupAddress || !mapkit) return;

        const delayFunction = setTimeout(() => {
            setIsLoading(true);
            const geocoder = new mapkit.Geocoder();
            geocoder.lookup(lookupAddress, (error: Error | null, data: GeocoderResponse) => {
                setIsLoading(false);

                if (error) {
                    console.error("Geocoding failed:", error);
                    return;
                }

                if (data?.results?.length > 0) {
                    setLookupAddressOptions(data.results);
                } else {
                    setLookupAddressOptions([]);
                }
            });
        }, 500); // debounce input

        return () => clearTimeout(delayFunction);
    }, [lookupAddress]);

    const pollJobStatus = async (id: string) => {
        try {
            const response = await jwtAxios.get(`/api/business/jobstatus?jobId=${id}`);
            if (!response) {
                throw new Error("Could not update status of GenAR");
            }

            const data = response.data;
            setJobMessage(data.message);

            if (!data.isComplete) {
                setTimeout(() => pollJobStatus(id), 2500);
            } else {

                if (data.step == -1) {
                    console.error(data.message);
                    dispatch(fetchError("Failed to create Real Estate Experience"));
                    setRequestLoading(false);
                    return;
                }

                setRequestLoading(false);
                dispatch(showMessage("Successfully created Real Estate Experience"));
                dispatch(getMyBusinessGroups(({ businessGroups }) => {
                    const group = businessGroups?.find(x => x.id === data.groupId) ?? null;
                    if (group) {
                        dispatch(setCurrentBusinessGroup(group));
                        prop.onComplete(group.id);
                    }
                }));
            }
        } catch (error) {
            console.error("Polling failed: ", error);
        }
    };


    const submitForm = () => {
        if (selectedLookupAddressOption && selectedLookupAddressOption.coordinate) {
            const address = selectedLookupAddressOption.formattedAddress ?? selectedLookupAddressOption.name;
            const coordinates = selectedLookupAddressOption.coordinate;
            const listingURL = url;
            setJobMessage("");
            setRequestLoading(true);
            dispatch(createRealEstateListing(address, coordinates, listingURL, result => {
                if(result) {
                    pollJobStatus(result);
                } else {
                    setRequestLoading(false);
                    console.error("Job submission failed");
                }
            }));
        }
    };

    return (
        <>
        <Box
            sx={{
            "& .MuiDialog-paper": {
                width: fullscreen ? "100%" : "70%",
                maxWidth: "1536px",
                backgroundColor: "white",
            },
            "& .MuiDialogContent-root": {
                overflowY: "auto",
                paddingLeft: 0,
                paddingRight: 0,
            },
            // minWidth: "800px",
            }}
        >
            <Box sx={{ 
            display: "flex", 
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2
            }}>
            <Typography sx={{
                fontSize: 30,
                fontWeight: Fonts.MEDIUM
            }}>Real Estate Experience GenAR</Typography>
            <Box sx={{
                display: "flex", 
                flexWrap: "wrap",
                flexShrink: 0,
                alignSelf: "flex-end",
                paddingBottom: "16px"
            }}>
                {/* Close / Cancel - Mode-agnostic. */}
                <Button     
                    variant="outlined"
                    sx={{ marginRight: 4 }}
                    onClick={prop.cancelSettingsForm}
                >
                    {prop.mode === "MOD_ONLY" ? "Close" : "Cancel"}
                </Button>
                {/* Save - General - Only visible outside of mod_only. */}
                {(prop.mode !== "MOD_ONLY" && prop.mode !== "READ_ONLY") && (
                    <Button
                    sx={{ marginRight: 2, flexShrink: 0 }}
                    variant="contained"
                    onClick={submitForm}
                    disabled={requestLoading}
                    >
                    Save
                    </Button>
                )}
            </Box>
            </Box>
            <Divider />
            <Box style={{ paddingTop: 4  }}>
            {requestLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        margin: "auto",
                        height: 'calc(100vh - 140px)'
                    }}
                >
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center",      alignItems: "center", gap: 4 }}>
                <CircularProgress/>
                <Typography variant="body1" sx={{ mb: 2, color: AppColor.darkBrand }}>
                    {jobMessage || "Starting..."}
                </Typography>
                </Box>
                </Box>
            ) : (
                <Paper sx={{ backgroundColor: "white" }}>
                    <Box sx={{ padding: 2 }}>
                        <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 1: Listing</Typography>
                        <Box sx={{ marginX: 2, marginY: 4, display: "flex", flexDirection: "column", alignItems: "flex-start", width: "70%"}}>
                        <Typography sx={{ fontWeight: Fonts.MEDIUM }}>Listing Address</Typography>
                            <Autocomplete
                                fullWidth
                                id="address"
                                noOptionsText="No locations"
                                filterOptions={(options) => options}
                                loading={isLoading}
                                value={selectedLookupAddressOption}
                                getOptionLabel={(option) => option.formattedAddress ?? option.name}
                                onKeyDown={e => {
                                e.key === 'Enter' && e.preventDefault();
                                }}
                                onChange={(event, value, reason) => {
                                switch (reason) {
                                    case "selectOption":
                                    if (value) {
                                        setSelectedLookupAddressOption(value);
                                        // Set current pin to selected location
                                        setUserLocation(value.coordinate);
                                    }
                                    break;
                                    case "clear":
                                    setSelectedLookupAddressOption(null);
                                    setLookupAddressOptions([]);
                                    break;
                                    default:
                                    break;
                                }
                                }}
                                renderOption={(props, option) => {
                                return (
                                    <Box component="li" {...props} sx={{ fontSize: 14 }} key={option.coordinate.latitude + option.coordinate.longitude}>
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <ReactCountryFlag
                                            countryCode={option.countryCode}
                                            svg
                                            style={{
                                            marginLeft: "0px",
                                            marginRight: "8px",
                                            marginBottom: "0px",
                                            width: '16px',
                                            height: '16px',
                                            }}></ReactCountryFlag>
                                        <Typography sx={{ mr: 2 }} fontWeight={Fonts.SEMI_BOLD}>{option.name}</Typography>
                                        </Box>
                                        <Typography
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: '2',
                                            WebkitBoxOrient: 'vertical'
                                        }}>{option.formattedAddress}</Typography>
                                    </Box>
                                    </Box>);
                                }
                    
                                }
                                renderInput={(params) => (
                                <TextField {...params}
                                    variant="outlined"
                                    placeholder="1234 Main Street, City, ST 12345"
                                    value={lookupAddress ?? ""}
                                    onChange={(event) => setLookupAddress(event.target.value)}
                                    InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                        <SearchIcon />
                                        </InputAdornment>
                                    )
                                    }}
                                />
                                )}
                                options={lookupAddressOptions} />
                        </Box>
                        <Box sx={{ marginX: 2, paddingY: 4, display: "flex", flexDirection: "column", alignItems: "flex-start", width: "70%" }}>
                        <Typography sx={{ fontWeight: Fonts.MEDIUM }}>Listing URL (optional)</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="https://www.yourMLS.com/listing/12345"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                    <LanguageIcon />
                                    </InputAdornment>
                                )
                            }}
                            onChange={(e) => setUrl(e.target.value)}
                        />
                        </Box>
                    </Box>
                </Paper>
            )}
            </Box>
        </Box>
        </>
        
    );

};

export default RealEstateListing;